import { useEffect, useRef, useState, useCallback } from 'react';

export default function TargetIndustries() {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
  ]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [bottomVisible, setBottomVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const setCardRef = useCallback((el: HTMLDivElement | null, index: number) => {
    cardRefs.current[index] = el;
  }, []);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    if (headerRef.current) headerObs.observe(headerRef.current);

    const bottomObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBottomVisible(true);
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    if (bottomRef.current) bottomObs.observe(bottomRef.current);

    // Each card gets its own observer for domino‑style reveal
    const cardObservers: IntersectionObserver[] = [];
    cardRefs.current.forEach((ref, index) => {
      if (!ref) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            // Stagger: reveal with a calm delay based on its column position
            const rowPosition = index % 3; // 0, 1, 2 for a 3‑col grid
            setTimeout(() => {
              setVisibleCards((prev) => {
                const next = [...prev];
                next[index] = true;
                return next;
              });
            }, rowPosition * 220);
            obs.unobserve(ref);
          }
        },
        { threshold: 0.06, rootMargin: '0px 0px -70px 0px' }
      );
      obs.observe(ref);
      cardObservers.push(obs);
    });

    return () => {
      headerObs.disconnect();
      bottomObs.disconnect();
      cardObservers.forEach((o) => o.disconnect());
    };
  }, []);

  const industries = [
    {
      icon: 'ri-government-line',
      title: 'Intelligence Agencies',
      description:
        'Government intelligence services and national security organizations seeking authoritative digital presence.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20government%20intelligence%20agency%20building%20exterior%20with%20sleek%20glass%20architecture%20and%20sophisticated%20security%20features%2C%20professional%20corporate%20atmosphere%2C%20dark%20moody%20lighting%20with%20subtle%20golden%20accents%2C%20premium%20architectural%20photography%20style&width=600&height=400&seq=industry-intel-v2&orientation=landscape',
      stats: 'High Authority',
      statsIcon: 'ri-verified-badge-line',
      number: '01',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Strategic Consulting',
      description:
        'Business intelligence firms, competitive analysis consultancies, and strategic advisory services.',
      image:
        'https://readdy.ai/api/search-image?query=Elegant%20executive%20boardroom%20with%20panoramic%20city%20views%20at%20dusk%2C%20sophisticated%20modern%20interior%20design%20with%20warm%20ambient%20lighting%2C%20premium%20business%20consulting%20atmosphere%2C%20professional%20corporate%20photography&width=600&height=400&seq=industry-consult-v2&orientation=landscape',
      stats: 'Premium Sector',
      statsIcon: 'ri-vip-crown-line',
      number: '02',
    },
    {
      icon: 'ri-cpu-line',
      title: 'AI & Technology',
      description:
        'Artificial intelligence platforms, machine learning companies, and data analytics providers.',
      image:
        'https://readdy.ai/api/search-image?query=Futuristic%20AI%20technology%20data%20center%20with%20glowing%20neural%20network%20visualizations%2C%20sophisticated%20server%20room%20with%20warm%20amber%20accent%20lighting%2C%20premium%20tech%20company%20atmosphere%2C%20cinematic%20professional%20photography&width=600&height=400&seq=industry-ai-v2&orientation=landscape',
      stats: 'Growing Market',
      statsIcon: 'ri-line-chart-line',
      number: '03',
    },
    {
      icon: 'ri-microscope-line',
      title: 'Research Institutions',
      description:
        'Think tanks, policy research centers, and academic institutions focused on global intelligence.',
      image:
        'https://readdy.ai/api/search-image?query=Prestigious%20university%20research%20library%20interior%20with%20elegant%20wooden%20architecture%20and%20warm%20golden%20lighting%2C%20sophisticated%20academic%20atmosphere%20with%20modern%20technology%20integration%2C%20premium%20institutional%20photography&width=600&height=400&seq=industry-research-v2&orientation=landscape',
      stats: 'Academic Trust',
      statsIcon: 'ri-award-line',
      number: '04',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'Non‑Profit Organizations',
      description:
        'International NGOs, humanitarian organizations, and global advocacy groups.',
      image:
        'https://readdy.ai/api/search-image?query=Elegant%20modern%20conference%20room%20with%20diverse%20group%20of%20professionals%20in%20formal%20business%20attire%20having%20a%20strategic%20meeting%20around%20a%20large%20table%2C%20warm%20natural%20lighting%20through%20floor%20to%20ceiling%20windows%2C%20sophisticated%20nonprofit%20headquarters%20interior%20with%20minimalist%20design%2C%20professional%20corporate%20photography%20with%20warm%20golden%20tones&width=600&height=400&seq=industry-ngo-v5&orientation=landscape',
      stats: 'Global Impact',
      statsIcon: 'ri-global-line',
      number: '05',
    },
    {
      icon: 'ri-safe-2-line',
      title: 'Investment Groups',
      description:
        'Domain investors, venture capital firms, and strategic asset portfolio managers.',
      image:
        'https://readdy.ai/api/search-image?query=Luxurious%20private%20equity%20investment%20office%20with%20sophisticated%20modern%20design%20and%20panoramic%20views%2C%20premium%20financial%20district%20atmosphere%20with%20warm%20golden%20hour%20lighting%2C%20elegant%20professional%20photography&width=600&height=400&seq=industry-invest-v2&orientation=landscape',
      stats: 'Asset Value',
      statsIcon: 'ri-money-dollar-circle-line',
      number: '06',
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="industries"
      className="relative py-32 bg-[#080808] overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[200px]" />
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-amber-600/5 rounded-full blur-[200px]" />

      {/* Decorative Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(217,169,78,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(217,169,78,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-24">
          {/* Floating Particles Around Header */}
          <div className="absolute left-1/2 -translate-x-1/2 top-20 w-[600px] h-[300px] pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  left: `${15 + i * 10}%`,
                  top: `${20 + (i % 3) * 25}%`,
                  background: i % 2 === 0 ? 'rgba(217,169,78,0.4)' : 'rgba(255,255,255,0.2)',
                  boxShadow: i % 2 === 0 ? '0 0 8px rgba(217,169,78,0.5)' : 'none',
                  animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                  animationDelay: `${i * 0.3}s`,
                }}
              />
            ))}
          </div>

          {/* Premium Badge */}
          <div
            className="relative inline-block mb-10"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.95)',
              transition:
                'opacity 1200ms cubic-bezier(0.22,1,0.36,1) 0ms, transform 1200ms cubic-bezier(0.22,1,0.36,1) 0ms',
            }}
          >
            {/* Outer Glow Rings */}
            <div
              className="absolute -inset-4 rounded-full opacity-30"
              style={{
                background:
                  'radial-gradient(circle, rgba(217,169,78,0.15) 0%, transparent 70%)',
                animation: 'pulse 3s ease-in-out infinite',
              }}
            />

            {/* Badge Container */}
            <div className="relative group">
              {/* Decorative Side Elements */}
              <div className="absolute -left-16 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <div
                  className={`h-px bg-gradient-to-r from-transparent to-amber-500/40 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    headerVisible ? 'w-8' : 'w-0'
                  }`}
                  style={{ transitionDelay: '550ms' }}
                />
                <div className="w-1.5 h-1.5 rotate-45 bg-amber-500/30 border border-amber-500/40" />
              </div>
              <div className="absolute -right-16 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rotate-45 bg-amber-500/30 border border-amber-500/40" />
                <div
                  className={`h-px bg-gradient-to-l from-transparent to-amber-500/40 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    headerVisible ? 'w-8' : 'w-0'
                  }`}
                  style={{ transitionDelay: '550ms' }}
                />
              </div>

              {/* Main Badge */}
              <div
                className="relative px-8 py-3 rounded-full overflow-hidden"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(217,169,78,0.12) 0%, rgba(217,169,78,0.05) 50%, rgba(217,169,78,0.12) 100%)',
                  border: '1px solid rgba(217,169,78,0.25)',
                  boxShadow: '0 0 30px rgba(217,169,78,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
              >
                <div
                  className="absolute inset-0 -skew-x-12"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent 0%, rgba(217,169,78,0.15) 50%, transparent 100%)',
                    animation: 'shimmer 4s ease-in-out infinite',
                  }}
                />
                <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
                <div className="relative flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <div className="relative">
                      <div
                        className="w-2 h-2 rounded-full bg-emerald-400"
                        style={{
                          boxShadow: '0 0 8px rgba(52,211,153,0.6)',
                        }}
                      />
                      <div
                        className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-40"
                      />
                    </div>
                  </div>
                  <div className="w-px h-4 bg-gradient-to-b from-transparent via-amber-500/30 to-transparent" />
                  <span className="text-[10px] tracking-[0.25em] text-amber-400/90 uppercase font-normal font-mono">
                    Perfect For
                  </span>
                  <div className="w-px h-4 bg-gradient-to-b from-transparent via-amber-500/30 to-transparent" />
                  <i className="ri-sparkling-2-fill text-amber-400/70 text-xs" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Title */}
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[500px] h-[150px] bg-amber-500/5 rounded-full blur-[80px]" />
            </div>

            <h2 className="relative">
              <span
                className="block text-4xl md:text-5xl lg:text-6xl font-light text-white/90 leading-tight mb-2 font-display"
                style={{
                  opacity: headerVisible ? 1 : 0,
                  transform: headerVisible ? 'translateY(0)' : 'translateY(18px)',
                  transition:
                    'opacity 1300ms cubic-bezier(0.22,1,0.36,1) 200ms, transform 1300ms cubic-bezier(0.22,1,0.36,1) 200ms',
                }}
              >
                Industries That
              </span>

              <span
                className="relative inline-block text-5xl md:text-6xl lg:text-7xl font-light leading-tight font-display"
                style={{
                  opacity: headerVisible ? 1 : 0,
                  transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition:
                    'opacity 1300ms cubic-bezier(0.22,1,0.36,1) 400ms, transform 1300ms cubic-bezier(0.22,1,0.36,1) 400ms',
                }}
              >
                <span
                  className="relative inline-block"
                  style={{
                    background:
                      'linear-gradient(135deg, #f5d78e 0%, #d4a843 25%, #f7e8b5 50%, #c9952e 75%, #f5d78e 100%)',
                    backgroundSize: '200% 200%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'gradientShift 4s ease-in-out infinite',
                  }}
                >
                  Command
                </span>
              </span>

              <span
                className="block text-4xl md:text-5xl lg:text-6xl font-light text-white/90 leading-tight mt-2 font-display"
                style={{
                  opacity: headerVisible ? 1 : 0,
                  transform: headerVisible ? 'translateY(0)' : 'translateY(18px)',
                  transition:
                    'opacity 1300ms cubic-bezier(0.22,1,0.36,1) 600ms, transform 1300ms cubic-bezier(0.22,1,0.36,1) 600ms',
                }}
              >
                This Domain
              </span>
            </h2>
          </div>

          {/* Decorative Divider */}
          <div
            className="flex items-center justify-center gap-4 mb-8"
            style={{
              opacity: headerVisible ? 1 : 0,
              transition: 'opacity 1200ms cubic-bezier(0.22,1,0.36,1) 750ms',
            }}
          >
            <div
              className={`h-px bg-gradient-to-r from-transparent to-amber-500/30 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                headerVisible ? 'w-16' : 'w-0'
              }`}
              style={{ transitionDelay: '850ms' }}
            />
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-amber-500/40" />
              <div className="w-2 h-2 rotate-45 border border-amber-500/40 bg-amber-500/10" />
              <div className="w-1 h-1 rounded-full bg-amber-500/40" />
            </div>
            <div
              className={`h-px bg-gradient-to-l from-transparent to-amber-500/30 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                headerVisible ? 'w-16' : 'w-0'
              }`}
              style={{ transitionDelay: '850ms' }}
            />
          </div>

          {/* Description */}
          <p
            className="text-white/45 max-w-2xl mx-auto text-lg leading-relaxed font-body"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(14px)',
              transition:
                'opacity 1200ms cubic-bezier(0.22,1,0.36,1) 900ms, transform 1200ms cubic-bezier(0.22,1,0.36,1) 900ms',
            }}
          >
            This premium domain serves as the{' '}
            <span className="text-amber-400/70 font-medium">perfect digital foundation</span>{' '}
            for organizations across multiple high‑value sectors.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {industries.map((industry, index) => {
            const isActive = activeIndex === index;
            const isCardVisible = visibleCards[index];
            return (
              <div
                key={index}
                ref={(el) => setCardRef(el, index)}
                className="group relative rounded-2xl cursor-pointer"
                style={{
                  opacity: isCardVisible ? 1 : 0,
                  transform: isCardVisible ? 'translateY(0) scale(1)' : 'translateY(35px) scale(0.96)',
                  transition:
                    'opacity 1200ms cubic-bezier(0.22,1,0.36,1), transform 1200ms cubic-bezier(0.22,1,0.36,1)',
                }}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {/* Outer Glow */}
                <div
                  className="absolute -inset-px rounded-2xl"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(217,169,78,0.4), rgba(217,169,78,0.1), rgba(217,169,78,0.3))',
                    opacity: isActive ? 1 : 0,
                    transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    filter: isActive ? 'blur(1px)' : 'blur(0px)',
                  }}
                />

                {/* Card Body */}
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(145deg, #111111 0%, #0c0c0c 50%, #0a0a0a 100%)',
                    border: '1px solid transparent',
                    borderColor: isActive
                      ? 'rgba(217,169,78,0.35)'
                      : 'rgba(255,255,255,0.06)',
                    transition:
                      'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: isActive
                      ? '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(217,169,78,0.08), inset 0 1px 0 rgba(217,169,78,0.1)'
                      : '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
                  }}
                >
                  {/* Shimmer Sweep */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none z-20">
                    <div
                      className="absolute inset-0 skew-x-12"
                      style={{
                        background:
                          'linear-gradient(90deg, transparent 0%, rgba(217,169,78,0.06) 45%, rgba(255,255,255,0.08) 50%, rgba(217,169,78,0.06) 55%, transparent 100%)',
                        transform: isActive
                          ? 'translateX(200%) skewX(-12deg)'
                          : 'translateX(-200%) skewX(-12deg)',
                        transition: 'transform 1200ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                  </div>

                  {/* Top Glow Line */}
                  <div
                    className="absolute top-0 left-0 right-0 h-px z-10"
                    style={{
                      background: isActive
                        ? 'linear-gradient(90deg, transparent, rgba(217,169,78,0.6), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(217,169,78,0.1), transparent)',
                      transition: 'background 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />

                  {/* Corner Frame Accents */}
                  <div
                    className="absolute top-3 left-3 w-6 h-6 border-l border-t rounded-tl-lg z-10 pointer-events-none"
                    style={{
                      borderColor: isActive ? 'rgba(217,169,78,0.4)' : 'transparent',
                      transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <div
                    className="absolute top-3 right-3 w-6 h-6 border-r border-t rounded-tr-lg z-10 pointer-events-none"
                    style={{
                      borderColor: isActive ? 'rgba(217,169,78,0.4)' : 'transparent',
                      transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <div
                    className="absolute bottom-3 left-3 w-6 h-6 border-l border-b rounded-bl-lg z-10 pointer-events-none"
                    style={{
                      borderColor: isActive ? 'rgba(217,169,78,0.25)' : 'transparent',
                      transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <div
                    className="absolute bottom-3 right-3 w-6 h-6 border-r border-b rounded-br-lg z-10 pointer-events-none"
                    style={{
                      borderColor: isActive ? 'rgba(217,169,78,0.25)' : 'transparent',
                      transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />

                  {/* Image Area */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-full object-cover object-top"
                      style={{
                        transition:
                          'transform 800ms cubic-bezier(0.4, 0, 0.2, 1), filter 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                        filter: isActive ? 'brightness(0.7)' : 'brightness(0.5)',
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/80 to-transparent" />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'radial-gradient(ellipse at 50% 80%, rgba(217,169,78,0.08) 0%, transparent 60%)',
                        opacity: isActive ? 1 : 0,
                        transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />

                    {/* Number Indicator */}
                    <div className="absolute top-5 left-5 z-10">
                      <span
                        className="text-[40px] font-extralight leading-none font-display"
                        style={{
                          color: isActive
                            ? 'rgba(217,169,78,0.35)'
                            : 'rgba(255,255,255,0.08)',
                          transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {industry.number}
                      </span>
                    </div>

                    {/* Stats Badge */}
                    <div className="absolute top-5 right-5 z-10">
                      <div
                        className="relative px-3.5 py-1.5 rounded-full overflow-hidden"
                        style={{
                          background: isActive
                            ? 'linear-gradient(135deg, rgba(217,169,78,0.25) 0%, rgba(217,169,78,0.12) 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                          backdropFilter: 'blur(16px)',
                          transition: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isActive
                            ? '0 4px 20px rgba(217,169,78,0.15), inset 0 0 12px rgba(217,169,78,0.05)'
                            : 'none',
                          border: isActive
                            ? '1px solid rgba(217,169,78,0.35)'
                            : '1px solid rgba(255,255,255,0.08)',
                        }}
                      >
                        <div className="relative flex items-center gap-1.5">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i
                              className={`${industry.statsIcon} text-xs`}
                              style={{
                                color: isActive
                                  ? 'rgba(217,169,78,0.9)'
                                  : 'rgba(255,255,255,0.4)',
                                transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            />
                          </div>
                          <span
                            className="text-[9px] tracking-[0.15em] uppercase font-medium font-mono"
                            style={{
                              color: isActive
                                ? 'rgba(217,169,78,0.9)'
                                : 'rgba(255,255,255,0.4)',
                              transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          >
                            {industry.stats}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="relative p-6 pt-5">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <div
                          className="absolute -inset-1.5 rounded-xl"
                          style={{
                            border: isActive
                              ? '1px solid rgba(217,169,78,0.2)'
                              : '1px solid transparent',
                            transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: isActive ? '0 0 15px rgba(217,169,78,0.1)' : 'none',
                          }}
                        />
                        <div
                          className="relative w-12 h-12 flex items-center justify-center rounded-xl overflow-hidden"
                          style={{
                            background: isActive
                              ? 'linear-gradient(135deg, rgba(217,169,78,0.25) 0%, rgba(217,169,78,0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                            border: isActive
                              ? '1px solid rgba(217,169,78,0.4)'
                              : '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: isActive
                              ? '0 0 20px rgba(217,169,78,0.15), inset 0 0 10px rgba(217,169,78,0.05)'
                              : 'none',
                          }}
                        >
                          <div
                            className="absolute inset-0"
                            style={{
                              background:
                                'radial-gradient(circle at center, rgba(217,169,78,0.2) 0%, transparent 70%)',
                              opacity: isActive ? 1 : 0,
                              transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                          <i
                            className={`${industry.icon} text-xl relative z-10`}
                            style={{
                              color: isActive ? 'rgba(217,169,78,1)' : 'rgba(255,255,255,0.5)',
                              transition:
                                'color 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                              transform: isActive ? 'scale(1.15)' : 'scale(1)',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <h3
                          className="text-lg font-semibold leading-tight font-mini-title"
                          style={{
                            color: isActive ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)',
                            transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {industry.title}
                        </h3>
                        <div
                          className="mt-1 h-px"
                          style={{
                            width: isActive ? '100%' : '0%',
                            background: 'linear-gradient(90deg, rgba(217,169,78,0.5), transparent)',
                            transition: 'width 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      </div>
                    </div>

                    <p
                      className="text-sm leading-relaxed mb-5 font-body"
                      style={{
                        color: isActive ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.35)',
                        transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {industry.description}
                    </p>

                    <div
                      className="flex items-center justify-between pt-4"
                      style={{
                        borderTop: isActive
                          ? '1px solid rgba(217,169,78,0.15)'
                          : '1px solid rgba(255,255,255,0.05)',
                        transition: 'border-color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 flex items-center justify-center rounded-md"
                          style={{
                            background: isActive
                              ? 'rgba(217,169,78,0.15)'
                              : 'rgba(255,255,255,0.05)',
                            transition: 'background 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <i
                            className="ri-sparkling-line text-xs"
                            style={{
                              color: isActive ? 'rgba(217,169,78,0.8)' : 'rgba(255,255,255,0.25)',
                              transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </div>
                        <span
                          className="text-[10px] tracking-[0.15em] uppercase font-mono"
                          style={{
                            color: isActive ? 'rgba(217,169,78,0.7)' : 'rgba(255,255,255,0.25)',
                            transition: 'color 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          Ideal Match
                        </span>
                      </div>
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full"
                        style={{
                          background: isActive ? 'rgba(217,169,78,0.15)' : 'rgba(255,255,255,0.04)',
                          border: isActive
                            ? '1px solid rgba(217,169,78,0.3)'
                            : '1px solid rgba(255,255,255,0.06)',
                          transition: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <i
                          className="ri-arrow-right-up-line text-sm"
                          style={{
                            color: isActive ? 'rgba(217,169,78,0.9)' : 'rgba(255,255,255,0.2)',
                            transition:
                              'color 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: isActive ? 'translate(1px, -1px)' : 'translate(0, 0)',
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px"
                    style={{
                      width: isActive ? '70%' : '0%',
                      background: 'linear-gradient(90deg, transparent, rgba(217,169,78,0.5), transparent)',
                      transition: 'width 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: isActive ? 1 : 0,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Statement */}
        <div ref={bottomRef} className="mt-24 text-center">
          <div
            className="inline-flex items-center gap-4 mb-8"
            style={{
              opacity: bottomVisible ? 1 : 0,
              transform: bottomVisible ? 'translateY(0)' : 'translateY(12px)',
              transition:
                'opacity 1200ms cubic-bezier(0.22,1,0.36,1) 0ms, transform 1200ms cubic-bezier(0.22,1,0.36,1) 0ms',
            }}
          >
            <div
              className={`h-px bg-gradient-to-r from-transparent to-amber-500/40 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                bottomVisible ? 'w-20' : 'w-0'
              }`}
              style={{ transitionDelay: '300ms' }}
            />
            <div className="relative">
              <div className="w-3 h-3 rotate-45 bg-amber-500/30 border border-amber-500/50" />
            </div>
            <div
              className={`h-px bg-gradient-to-l from-transparent to-amber-500/40 transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                bottomVisible ? 'w-20' : 'w-0'
              }`}
              style={{ transitionDelay: '300ms' }}
            />
          </div>
          <p
            className="text-xl text-white/40 max-w-2xl mx-auto font-light font-body italic"
            style={{
              opacity: bottomVisible ? 1 : 0,
              transform: bottomVisible ? 'translateY(0)' : 'translateY(12px)',
              transition:
                'opacity 1200ms cubic-bezier(0.22,1,0.36,1) 350ms, transform 1200ms cubic-bezier(0.22,1,0.36,1) 350ms',
            }}
          >
            &ldquo;A domain transcends industries - it defines leadership in the intelligence
            space.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}
